import c from 'classnames';
import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Outlet, useLocation, useMatch, useNavigate } from 'react-router-dom';

import { BackButton, useScrollMemo } from '@repo/ui';
import { ScrollHandler } from '@repo/utils';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { useScrollDisplay } from 'hooks/useScrollDisplay';
import useAccountsAndKits from 'services/account/useAccountsAndKits';

import {
  ContainerSizes,
  Header,
  PageContainer,
  Sidebar,
  Spinfinity,
} from 'components';
import ResultsAnnouncementBanner from 'components/ResultsAnnouncementBanner/ResultsAnnouncementBanner';
import useFeature from 'services/features/useFeature';
import MobileNav from '../../MobileNav/MobileNav';
import { TinyAI, TinyAIProvider } from '../../TinyAI';

import styles from './ResponsiveLayout.module.css';

const scrollHandler = ScrollHandler.instance;

export const PageAnalytics = ({ children }: { children: React.ReactNode }) => {
  useAnalytics().pageEvent();
  return <>{children}</>;
};
const ResponsiveLayout = () => {
  useAccountsAndKits();
  const { currentTinyAccount, currentKit } = useCurrentKitAndTinyAccount();
  const navigate = useNavigate();
  const appWrapperRef = useRef<HTMLDivElement>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const toggleMobileMenu = useCallback(() => setIsMobileMenuOpen(o => !o), []);
  const { pathname, hash } = useLocation();
  const { isFeatureEnabled: showResultsV2Banner } =
    useFeature('results_v2_banner');

  const isAllMetricsPage = !!useMatch('/results/:kitId/insights/all-insights');
  const isNewAllMetricsPage = !!useMatch('/results/:kitId/metrics/all-metrics');
  const isConditionsHomePage = !!useMatch('/results/:kitId/conditions/metrics');
  // Disabled where useHashScroll owns the scroll, or it fights a deep link.
  useScrollMemo(useLocation, {
    pathnameFunctionKey: 'pathname',
    containerRef: appWrapperRef,
    disabled: isAllMetricsPage || isNewAllMetricsPage || isConditionsHomePage,
  });
  const isResultsKitRoute = !!useMatch('/results/:kitId/*');
  const isActionPlanPage = useMatch('/action-plan/:kitId/*');
  const isTopLevelResults = useMatch({ path: '/results/:kitId', end: true });
  const isTopLevelActionPlan = useMatch({
    path: '/action-plan/:kitId',
    end: true,
  });
  const allMetricsScrollPosKey = `allMetricsScrollPos_${currentKit?.id}`;

  const isBackButtonVisible = useScrollDisplay({
    resetOnNavigate: true,
    threshold: 50,
    action: 'show',
    lockStateOnThreshold: true,
    scrollableElRef: appWrapperRef,
  });

  const goBack = () => {
    scrollHandler.setScrollRestoration();
    navigate(-1);
  };

  const showBackButton =
    (isResultsKitRoute || isActionPlanPage) &&
    !(isTopLevelResults || isTopLevelActionPlan) &&
    isBackButtonVisible &&
    !isNewAllMetricsPage &&
    // Renders its own inline one, so this would float over the organ selector.
    !isConditionsHomePage;

  useEffect(() => {
    const scrollContainer = appWrapperRef.current;

    const handleScroll = () => {
      if (scrollContainer) {
        sessionStorage.setItem(
          allMetricsScrollPosKey,
          scrollContainer.scrollTop.toString(),
        );
      }
    };

    if ((isAllMetricsPage || isNewAllMetricsPage) && scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if ((isAllMetricsPage || isNewAllMetricsPage) && scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isAllMetricsPage, isNewAllMetricsPage, allMetricsScrollPosKey]);

  useLayoutEffect(() => {
    const shouldRestore =
      (isAllMetricsPage || (isNewAllMetricsPage && !hash)) &&
      appWrapperRef.current;

    if (!shouldRestore) return;

    const savedPosition = sessionStorage.getItem(allMetricsScrollPosKey);
    if (!savedPosition) return;

    const scrollPosition = parseInt(savedPosition, 10);
    const container = appWrapperRef.current;
    if (!container) return;
   
    container.scrollTop = scrollPosition;

    const rafId = requestAnimationFrame(() => {
      if (appWrapperRef.current) {
        appWrapperRef.current.scrollTop = scrollPosition;
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [isAllMetricsPage, isNewAllMetricsPage, allMetricsScrollPosKey, hash]);

  useEffect(() => {
    // All-metrics pages own their scroll; so does the conditions home on a deep
    // link (useHashScroll drives it) — never reset those to top here.
    if (
      appWrapperRef.current &&
      !isAllMetricsPage &&
      !isNewAllMetricsPage &&
      !(isConditionsHomePage && hash)
    ) {
      appWrapperRef.current.scrollTo(0, 0);
    }
  }, [
    pathname,
    isAllMetricsPage,
    isNewAllMetricsPage,
    isConditionsHomePage,
    hash,
  ]);

  return (
    <TinyAIProvider>
      <div className={styles.rootContainer}>
        <div className={styles.sidebar}>
          <Sidebar />
        </div>
        <div className={styles.appWrapper} ref={appWrapperRef} id='appWrapper'>
          <div className={styles.contentContainer} id='content-container'>
            <PageContainer
              size={ContainerSizes.LG}
              className={styles.pageContainer}
            >
              {/* Render header for results pages */}
              {showBackButton && (
                <div
                  className={c(
                    styles.floatingBackButtonContainer,
                    'hide-on-desktop',
                  )}
                >
                  <BackButton variant='button' goBack={goBack} />
                </div>
              )}

              {isResultsKitRoute && isTopLevelResults && (
                <>
                  {showResultsV2Banner && (
                    <div className={styles.banner}>
                      <ResultsAnnouncementBanner
                        mainText='New look, same results'
                        secondaryText='Read our blog on how the summary page has changed.'
                        linkText='Read now'
                        linkUrl='https://www.tinyhealth.com/blog/unlock-your-microbiome-insights-with-our-new-results-view'
                      />
                    </div>
                  )}
                  <Header
                    type='full'
                    title={`${currentTinyAccount?.first_name}'s results`}
                    containerRef={appWrapperRef}
                  />
                </>
              )}
              <Suspense
                fallback={
                  <div className={styles.spinfinityWrapper}>
                    <Spinfinity />
                  </div>
                }
              >
                <PageAnalytics>
                  <div className={styles.contentWrapper}>
                    <Outlet />
                  </div>
                </PageAnalytics>
              </Suspense>
            </PageContainer>
          </div>
        </div>
        <MobileNav isOpen={isMobileMenuOpen} onToggle={toggleMobileMenu} />
        <TinyAI scrollableElRef={appWrapperRef} isHidden={isMobileMenuOpen} />
      </div>
    </TinyAIProvider>
  );
};
export default ResponsiveLayout;
